import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { PhoneOutgoing, CheckCircle2, MailWarning, Repeat } from 'lucide-react';

const CH = { letter: 'Letter', email: 'Email', linkedin: 'LinkedIn', call: 'Call' };

export function OutreachHealthSection() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.getOutreachHealth().then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const overdue = [...data.overdue_calls, ...data.overdue_other];
  const attention = overdue.length + data.due_today.length + data.bounced.length;

  if (attention === 0) {
    return (
      <div className="border border-emerald-500/25 bg-emerald-500/5 rounded-sm p-4 flex items-center gap-2" data-testid="outreach-health-widget">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-medium text-foreground">Outreach Health</span>
        <span className="text-xs text-emerald-400">
          {data.active} active sequence{data.active === 1 ? '' : 's'} — no touches due, no overdue calls, no bounces.
        </span>
      </div>
    );
  }

  const chip = (key, label, mono, cls, pid) => (
    <button key={key} onClick={() => navigate(`/projects/${pid}#section-outreach`)}
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border transition-colors ${cls}`}
      data-testid={`outreach-health-item-${key}`}>
      <span className="truncate max-w-[200px]">{label}</span>
      <span className="opacity-70 font-mono">{mono}</span>
    </button>
  );

  return (
    <div className={`border rounded-sm p-3 sm:p-4 ${overdue.length > 0 ? 'bg-red-500/5 border-red-500/30' : 'bg-amber-500/5 border-amber-500/25'}`}
      data-testid="outreach-health-widget">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <PhoneOutgoing className={`w-4 h-4 ${overdue.length > 0 ? 'text-red-400' : 'text-amber-400'}`} />
        <span className="text-sm font-medium text-foreground">Outreach Health</span>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Repeat className="w-3 h-3" /> {data.active} active
        </span>
        <span className="text-xs text-muted-foreground" data-testid="outreach-health-summary">
          {data.due_today.length > 0 && <span className="text-sky-300 font-bold">{data.due_today.length} due today</span>}
          {data.due_today.length > 0 && overdue.length > 0 && ' · '}
          {overdue.length > 0 && <span className="text-red-400 font-bold">{overdue.length} overdue ({data.overdue_calls.length} call{data.overdue_calls.length === 1 ? '' : 's'})</span>}
          {(data.due_today.length > 0 || overdue.length > 0) && data.bounced.length > 0 && ' · '}
          {data.bounced.length > 0 && <span className="text-orange-300 font-bold">{data.bounced.length} bounce{data.bounced.length === 1 ? '' : 's'} (30d)</span>}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {overdue.map((e, i) => chip(`overdue-${i}`,
          `${e.contact_name} · ${CH[e.channel] || e.channel}${e.fallback_from ? ` (statt ${CH[e.fallback_from]})` : ''}`,
          `${e.days_over}d over`,
          'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20', e.project_id))}
        {data.due_today.map((e, i) => chip(`due-${i}`,
          `${e.contact_name} · ${CH[e.channel] || e.channel}`,
          'today',
          'bg-sky-500/10 border-sky-500/25 text-sky-300 hover:bg-sky-500/20', e.project_id))}
        {data.bounced.map((e, i) => chip(`bounced-${i}`,
          `${e.contact_name} · email bounced`,
          e.next_channel ? `next: ${CH[e.next_channel] || e.next_channel} ${e.next_date || ''}` : e.bounced_at,
          'bg-orange-500/10 border-orange-500/30 text-orange-300 hover:bg-orange-500/20', e.project_id))}
      </div>
    </div>
  );
}
